
import React, { useState, useEffect } from "react"

import { Link, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/useAuth"
import { useUserPresence } from "@/hooks/useUserPresence"
import { useNotificationIntegration } from "@/hooks/useNotificationIntegration"
import { AppSidebar } from "./AppSidebar"
import { 
  SidebarInset, 
  SidebarProvider, 
  SidebarTrigger 
} from "@/components/ui/sidebar"
import { Separator } from "@/components/ui/separator"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import NotificationBell from "./NotificationBell"
import BtnSair from "./BtnSair"
import { BackgroundSyncManager } from "./BackgroundSyncManager"


interface LayoutProps {
  children: React.ReactNode
}

const getPageTitle = (pathname: string): string => {
  const routes: { [key: string]: string } = {
    '/dashboard': 'Dashboard',
    '/moradores': 'Moradores',
    '/unidades': 'Unidades',
    '/visitantes': 'Visitantes',
    '/encomendas': 'Encomendas',
    '/ocorrencias': 'Ocorrências',
    '/avisos': 'Avisos',
    '/acesso': 'Liberar Acesso',
    '/agenda': 'Liberar Acesso',
    '/areas-comuns': 'Áreas Comuns',
    '/mensagens': 'Mensagens',
    '/ponto-eletronico': 'Ponto Digital',
    '/ponto-digital': 'Ponto Digital',
    '/folhas-ponto': 'Folhas de Ponto',
    '/relatorios': 'Relatórios',
    '/configuracoes': 'Configurações',
    '/pets': 'Pets',
    '/superadmin': 'Super Admin',
    '/superadmin/organizacoes': 'Gestão de Organizações',
    '/superadmin/usuarios': 'Gestão de Usuários',
    '/superadmin/licencas': 'Chaves de Licença',
    '/superadmin/dados': 'Gestão de Dados',
    '/superadmin/analytics': 'Métricas e Analytics',
    '/gestao-comercial': 'Gestão Comercial',
    '/gestao-comercial/propostas': 'Propostas',
    '/gestao-comercial/contratos': 'Contratos',
    '/gestao-comercial/reunioes': 'Reuniões e Atas',
    '/gestao-comercial/horas': 'Horas Técnicas',
    '/reunioes': 'Reuniões Online',
    '/financeiro': 'Gestão Financeira',
    '/acessos': 'Controle de Acessos',
    '/destinos': 'Destinos'
  }
  
  // Handle dynamic routes for meetings
  if (pathname.startsWith('/reunioes/')) {
    return 'Reuniões Online'
  }
  
  return routes[pathname] || 'Página'
}

export const Layout = ({ children }: LayoutProps) => {
  const { profile } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  
  // Inicializar detecção de presença de usuários
  useUserPresence()
  
  // Inicializar integração de notificações push
  const notificationSystem = useNotificationIntegration()
  
  useEffect(() => {
    console.log('📱 [Layout] Sistema de notificações inicializado')
  }, [])

  // Remove redirect logic - ProtectedRoute handles this
  // Layout assumes it's only rendered when user is authenticated

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Carregando perfil...</p>
        </div>
      </div>
    )
  }

  const pageTitle = getPageTitle(location.pathname)

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4 w-full">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink asChild>
                    <Link to="/dashboard">Dashboard</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {location.pathname !== '/dashboard' && (
                  <>
                    <BreadcrumbSeparator className="hidden md:block" />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{pageTitle}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="ml-auto flex items-center gap-2">
              <NotificationBell />
              <BtnSair />
            </div>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {children}
        </div>
        <BackgroundSyncManager />
      </SidebarInset>
    </SidebarProvider>
  )
}
